<template>
  <gradient-wrapper>
    <form-card :show-action-section="false">
      <headline :text="welcomeText" />
      <div
        id="ask-for-email"
        class="mb-4"
      >
        <span class="sn-font-primary">{{ signInFirstTimeHere }}&nbsp;</span>
        <a
          class="sn-font-weight-semi-bold"
          @click="registerClicked"
        >
          {{ signInRegister }}
        </a>
      </div>
      <v-form
        ref="form"
        @submit.prevent
      >
        <sn-label
          class="mb-2"
          :title="signInEmailText"
        />
        <sn-text
          v-model="userEmail"
          sn-input-props_identifier="user-email"
          :sn-input-props_rules="rules.email"
          sn-input-props_validate-on-blur
          @blur="validateAndPersistEmail"
          @focus="clearError"
          @keyup.native.enter="signInClicked"
        />
        <sn-label
          class="mb-2"
          :title="signInPasswordText"
        />
        <sn-password
          v-model="userPassword"
          sn-input-props_identifier="user-password"
          sn-input-props_validate-on-blur
          :sn-input-props_rules="rules.password"
          :sn-input-props_error-messages="errorMessage"
          @focus="clearError"
          @keyup.native.enter="signInClicked"
        />
      </v-form>
      <v-row no-gutters>
        <sn-btn
          class="full-width"
          :text="signInSignInButton"
          :loading="btnLoading"
          @click="signInClicked"
        />
      </v-row>
      <v-row
        class="mt-9 mb-10"
        no-gutters
        :justify="$vuetify.breakpoint.mdAndDown ? 'center' : 'start'"
      >
        <a
          class="sn-font-weight-semi-bold"
          :loading="btnLoading"
          @click="recoverPasswordClicked"
        >
          {{ signInForgotYourPassword }}
        </a>
      </v-row>
    </form-card>
  </gradient-wrapper>
</template>

<script>
import FormCard from './Shared/FormCard.vue'
import GradientWrapper from './Shared/GradientWrapper.vue'
import snText from '~/components/snui/v2/snText/snText'
import snBtn from '~/components/snui/v2/snBtn/snBtn.vue'
import snLabel from '~/components/snui/v2/snLabel/snLabel.vue'
import snPassword from '~/components/snui/v2/snPassword/snPassword.vue'
import cookie from 'js-cookie'
import snValidationsService from '~/services/snValidationsService.js'
import fetchServicerMixin from './Shared/fetchServicerMixin'
import snThemeMixin from '~/components/common/snThemeMixin'
import Headline from './Shared/Headline.vue'
import homehubMixin from './Shared/homehubMixin.js'
import {
  initialize,
  requestSignIn,
  signIn,
  requestHelp,
  start
} from './Shared/requests.js'

export default {
  name: 'SignIn',

  components: {
    GradientWrapper,
    FormCard,
    Headline,
    snBtn,
    snLabel,
    snText,
    snPassword
  },
  mixins: [homehubMixin, snThemeMixin, fetchServicerMixin],
  data () {
    return {
      userEmail: this.$store.userEmail || this.$route.query.email,
      userPassword: undefined,
      errorMessage: undefined,
      canTransitionHere: true,
      rules: {
        email: [
          (v) => !!v || this.signInEmailErrorMessage,
          (v) => this.emailIsValid || this.signInInvalidEmailErrorMessage
        ],
        password: [
          (v) => !!v || this.signInPasswordErrorMessage
        ]
      }
    }
  },
  computed: {
    headerClass () {
      return this.$vuetify.breakpoint.mdAndDown ? 'sn-display-2' : 'sn-h2'
    },
    emailIsValid () {
      return !!this.userEmail && snValidationsService.email(this.userEmail)
    },
    authGuid () {
      return this.$store.authGuid || cookie.get('auth_guid')
    },
    welcomeText () {
      return this.$store.borrowerWebContent?.welcome
    },
    signInFirstTimeHere () {
      return this.$store.borrowerWebContent?.signInFirstTimeHere
    },
    signInRegister () {
      return this.$store.borrowerWebContent?.signInRegister
    },
    signInEmailText () {
      return this.$store.borrowerWebContent?.signInEmailText
    },
    signInPasswordText () {
      return this.$store.borrowerWebContent?.signInPasswordText
    },
    signInForgotYourPassword () {
      return this.$store.borrowerWebContent?.signInForgotYourPassword
    },
    signInSignInButton () {
      return this.$store.borrowerWebContent?.signInSignInButton
    },
    signInEmailErrorMessage () {
      return this.$store.borrowerWebContent?.signInEmailErrorMessage || 'We need your email in order to help you sign in.'
    },
    signInPasswordErrorMessage () {
      return this.$store.borrowerWebContent?.signInPasswordErrorMessage || 'We need your password in order to help you sign in.'
    },
    signInInvalidEmailErrorMessage () {
      return this.$store.borrowerWebContent?.signInInvalidEmailErrorMessage || 'This doesn\'t appear to be a valid email.'
    },
    incorrectUsernameOrPass () {
      return this.$store.borrowerWebContent?.incorrectUsernameOrPass
    },
    resetYourPasswordErrorMessage () {
      return this.$store.borrowerWebContent?.resetYourPasswordErrorMessage
    }
  },
  beforeCreate () {
    // used by snThemeMixin to fetch custom themes
    this.$store.activationCode = this.$route.params.code
  },
  async mounted () {
    const calls = [
      {
        callback: initialize,
        onSuccess: () => {
          this.$store.authGuid = this.response
          cookie.set('auth_guid', this.response)
        }
      },
      {
        callback: () => requestSignIn({
          authGuid: this.authGuid,
          activationCode: this.$route.params.code
        })
      }
    ]

    const handleSuccess = () => {
      this.$store.currentAuthState = 'auth_sign_in'
      this.validateAndPersistEmail()
    }

    this.chainApiCalls(calls, handleSuccess)
  },
  methods: {
    clearError () {
      this.errorMessage = undefined
    },
    async registerClicked () {
      this.$store.userClickedForgotPassword = false

      const calls = [
        {
          callback: initialize,
          onSuccess: () => {
            this.$store.authGuid = this.response
            cookie.set('auth_guid', this.response)
          }
        },
        { callback: () => start(this.$store.authGuid) }
      ]

      const handleSuccess = () => {
        this.persistState()
        this.performTransition()
      }

      this.chainApiCalls(calls, handleSuccess)
    },
    async recoverPasswordClicked () {
      this.$store.userClickedForgotPassword = true
      this.response = await requestHelp(this.$store.authGuid)
      if (this.serverError) return this.handleApiError()

      this.persistState()
      this.performTransition()
    },
    async signInClicked () {
      if (!this.$refs.form.validate() || this.btnLoading) return
      this.validateAndPersistEmail()
      this.btnLoading = true
      this.$store.userClickedForgotPassword = false

      this.response = await signIn({
        authGuid: this.authGuid,
        authenticityToken: document.getElementsByName('csrf-token')[0].content,
        activationCode: this.$route.params.code,
        userEmail: this.userEmail,
        userPassword: this.userPassword
      })
      if (this.serverError) {
        if (this.response?.data?.error === 'Incorrect email or password') {
          this.btnLoading = false
          this.errorMessage = this.incorrectUsernameOrPass
        } else if (this.response?.data?.error === 'Sorry, your account is locked. Please reset your password to unlock.') {
          this.btnLoading = false
          this.errorMessage = this.resetYourPasswordErrorMessage
        } else {
          return this.handleApiError()
        }
      } else {
        this.persistState()
        this.performTransition()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/snui';

.full-width {
  width: 100%;
}

.error-div {
  color: red;
}

a {
  font-family: $font-family-primary;
  font-weight: $font-weight-semi-bold;
  color: $sn-primary-04;
  text-decoration: underline;
}

@media screen and (max-width: $medium-screen-size) {
  h1, div#ask-for-email, div#forgot-your-password {
    margin: 0 auto;
  }
}

</style>
